import React from 'react';
import { Chart } from 'react-google-charts';
import { PivotTableData } from '../../models/PulseData';
import logger from '../../utils/logger';

interface PivotSankeyProps {
  data: PivotTableData;
  title?: string;
}

const PivotSankey: React.FC<PivotSankeyProps> = ({ data, title }) => {
  if (!data || !data.rows || data.rows.length === 0) {
    return <p>No data available to render Sankey diagram.</p>;
  }

  const columns =  data.columnValues;
  const rowsData = data.rows;
  const sourceNode = data.grandTotal + ' Errors'; // Starting node for the Sankey: title

  // Prepare data for Sankey diagram
  // Format: [Source, Target, Value]
  const sankeyData: (string | number)[][] = [['From', 'To', 'Count']];

  const MAX_VALUE_LENGTH = 50;

  const renderFieldValue = (fieldName: string, value: any ) => {

    const valueToRender = 
    ( value === "$deriq.other$"
      ? "Others (" + fieldName  + ")"
      : (value === "" 
        ? "(Empty)" 
        : typeof value === 'string' && (value as string).length > MAX_VALUE_LENGTH
        ? `${(value as string).substring(0, MAX_VALUE_LENGTH)} ...`
      : String(value ?? '')
      )
    );

    return valueToRender
  }  

  // 1. Grand Total to Columns (Dates/ColumnValues)
  columns.forEach(col => {
    const columnTotal = data.columnTotals[col];
    if (columnTotal > 0) {
      sankeyData.push([sourceNode, `On ${col}`, columnTotal]);
    }
  });

  // 2. Columns (Dates/ColumnValues) to Rows (Services/RowValues)
  rowsData.forEach(row => {
    columns.forEach(col => {
      const cellValue = row.valuesByColumn[col];
      if (cellValue > 0) {
        sankeyData.push([`On ${col}`, 
            renderFieldValue(data.fieldNames.row, row.rowValue),
            cellValue]);
      }
    });
  });

  if (sankeyData.length <= 1) {
    logger.warn('Not enough data points to render a meaningful Sankey diagram.', { data });
    return <p>Not enough data relationships to display Sankey diagram.</p>;
  }

  let sankeyHeight = Math.max(200, sankeyData.length * 10);
  sankeyHeight = Math.min( sankeyHeight, 400);

  // //  'var(--text-color)', Use app's text color for dark/light mode
  const sankeyOptions = {
    height: sankeyHeight, // Dynamic height based on number of links
    sankey: {
      node: {
        colors: ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99', '#e31a1c', '#fdbf6f', '#ff7f00', '#cab2d6', '#6a3d9a'], // A palette of colors
        label: { 
          fontName: 'Arial', 
          fontSize: 16,
          color: '#DBB6A9',
          bold: false, 
          italic: false 
        },
        interactivity: true, // Enable node interactivity
      },
      link: {
        colorMode: 'source', // 'source', 'target', 'gradient', or 'none'
        colors: ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99', '#e31a1c', '#fdbf6f', '#ff7f00', '#cab2d6', '#6a3d9a'] // Palette for links if not gradient
      },
      iterations: 24, // Default is 32. More iterations for complex diagrams.
    },
    backgroundColor: 'transparent', // Make chart background transparent
    chartArea: {
        left: '5%',
        top: '5%',
        width: '80%',
        height: '80%'
    },
    tooltip: {
        textStyle: { color: 'var(--text-color-secondary)' }, // Tooltip text color
        showColorCode: true
    }
  };

  return (
    <div className="pivot-sankey-container">
      <h4>{title ? `Flow: ${title}` : 'Error Flow Sankey Diagram'}</h4>
      <Chart
        chartType="Sankey"
        width="95%"        
        height={`${sankeyOptions.height}px`}
        data={sankeyData}
        options={sankeyOptions}
        loader={<div>Loading Chart...</div>}
      />
    </div>
  );
};

export default PivotSankey;